import { useEffect, useMemo, useRef, useState } from "react";
import {
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import { App } from "@back4app2/sdk";
import { DsButton, ScreenHead, SurfaceCard } from "../components/ds";
import type { SheetRow } from "../components/ds";
import { AlertIcon, UploadIcon } from "../components/ds/icons";
import CodeViewer from "../components/UploadSource/CodeViewer";
import UploadNewCodeDialog from "../components/UploadSource/UploadNewCodeDialog";
import {
  ApiV2Error,
  AppSource,
  archiveDeployment,
  archiveSource,
  DeploymentActions,
  getAppSource,
  getDeploymentActions,
} from "../utils/apiV2";
import { downloadArchive } from "../utils/archiveDownload";
import { appSourceKind } from "../utils/appSource";
import { canReadSource } from "../utils/permissions";
import { canRunAppActions } from "../components/MyAppActions";

/**
 * Source Code: the app's uploaded code, read-only, with the rest of the
 * window to read it in. It used to be a "View files" dialog over Settings,
 * which capped the editor at two thirds of the screen.
 *
 * Shows the app's latest code, or one deployment's (`?deployment=`, what the
 * Deployments list's code button opens) while that code is kept. The chip
 * beside the title switches between the ones that still are.
 *
 * Only for apps that deploy from uploads: a GitHub app's code is on GitHub,
 * and an image app has none of its own here.
 */

interface Version {
  deploymentId: string;
  number: number;
  hash: string;
  isLive: boolean;
}

const VersionPicker = ({
  current,
  versions,
  onPick,
  variant = "chip",
}: {
  current: { number?: number; hash: string; isLive?: boolean };
  versions: Version[];
  onPick: (version: Version) => void;
  /** chip: beside the title (desktop). toolbar: the phone's picker row, where
   * a dot says live instead of the pill. */
  variant?: "chip" | "toolbar";
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node))
        setIsOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen]);

  const canPick = versions.length > 1;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={!canPick}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className={`b4-focus flex items-center gap-2 border border-navy-400 bg-navy-800 font-courrier disabled:cursor-default enabled:hover:border-navy-300 ${
          variant === "toolbar"
            ? "h-11 rounded-card px-3.5 text-ds-135"
            : "rounded-control px-2.5 py-1 text-ds-125"
        }`}
      >
        {variant === "toolbar" ? (
          <span
            className={`w-2 h-2 flex-none rounded-full ${
              current.isLive ? "bg-state-ok-soft" : "bg-navy-300"
            }`}
            aria-label={current.isLive ? "Live" : "Not live"}
          />
        ) : null}
        {current.number ? (
          <span className="text-white">#{current.number}</span>
        ) : null}
        <span className="text-navy-200">{current.hash}</span>
        {canPick ? (
          <span className="text-[10px] text-navy-300" aria-hidden>
            {"▾"}
          </span>
        ) : null}
      </button>
      {isOpen ? (
        <ul
          role="listbox"
          className="absolute left-0 top-full z-20 mt-1.5 min-w-full m-0 p-1 list-none rounded-card border border-navy-500 bg-navy-800 shadow-[0_12px_32px_rgba(0,0,0,0.45)]"
        >
          {versions.map((version) => (
            <li key={version.deploymentId}>
              <button
                type="button"
                role="option"
                aria-selected={version.hash === current.hash}
                onClick={() => {
                  setIsOpen(false);
                  onPick(version);
                }}
                className="b4-focus w-full flex items-center gap-3 whitespace-nowrap rounded-control px-2.5 py-1.5 text-left font-courrier text-ds-125 hover:bg-navy-600"
              >
                <span className="text-white">#{version.number}</span>
                <span className="text-navy-200">{version.hash}</span>
                {version.isLive ? (
                  <span className="ml-auto font-inter text-ds-115 text-state-ok-soft">
                    Live
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

// Only when the code shown is the one running: "Not live" beside every
// other version said nothing the chip's missing pill did not.
const LivePill = ({ isLive }: { isLive: boolean }) =>
  isLive ? (
    <span className="flex items-center gap-1.5 rounded-full border border-state-ok/30 bg-state-ok/[0.12] px-2.5 py-0.5 text-ds-115 text-state-ok-soft">
      <span className="w-1.5 h-1.5 rounded-full bg-state-ok-soft" />
      Live
    </span>
  ) : null;

const MyAppSource = () => {
  const { app, setScreenActions } = useOutletContext<{
    app: App;
    /** The phone header's "⋯": this screen's rows go first in it. */
    setScreenActions: (rows: SheetRow[]) => void;
  }>();
  const env = app?.mainService?.mainServiceEnvironment;
  const isUpload =
    appSourceKind(env, !!app?.mainService?.repository) === "UPLOAD";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const deploymentId = searchParams.get("deployment") || undefined;

  const [source, setSource] = useState<AppSource | null>(null);
  const [actions, setActions] = useState<Record<
    string,
    DeploymentActions
  > | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  // Uploading code is a deploy: the same gate as the Action menu's Deploy and
  // the rollback, and the one the API applies (deploy access, app not blocked
  // or awaiting payment). Settings › Build & deploy asks the same.
  const canUpload = !!app && canRunAppActions(app);
  const canRead = !!app && canReadSource(app);

  // The support launcher floats over the corner the editor's save bar uses;
  // this screen goes without it (index.css).
  useEffect(() => {
    document.body.classList.add("b4a2-source-screen");
    return () => document.body.classList.remove("b4a2-source-screen");
  }, []);

  // Bumped when the code changed from here (the editor saved, or updated
  // under its edits), so the latest is read again.
  const [codeVersion, setCodeVersion] = useState(0);

  // What the viewer offers on the phone (copy, download, export, the open
  // file's edits): rows of the header's "⋯", with Upload first.
  const [viewerRows, setViewerRows] = useState<SheetRow[]>([]);
  useEffect(() => {
    setScreenActions([
      ...(canUpload
        ? [{ label: "Upload files", onClick: () => setIsUploadOpen(true) }]
        : []),
      ...viewerRows,
    ]);
    return () => setScreenActions([]);
  }, [setScreenActions, canUpload, viewerRows]);

  useEffect(() => {
    if (!app || !isUpload || !canRead) return;
    let cancelled = false;
    setLoadError(null);
    getAppSource(app.id)
      .then((result) => !cancelled && setSource(result))
      .catch(
        (e) =>
          !cancelled &&
          setLoadError(
            e instanceof ApiV2Error
              ? e.message
              : "This app's code could not be loaded."
          )
      );
    // Only for the chip: without it the screen still shows the code.
    getDeploymentActions(app.id)
      .then((result) => !cancelled && setActions(result))
      .catch((e) => console.error("could not load deployment versions", e));
    return () => {
      cancelled = true;
    };
  }, [app, isUpload, canRead, codeVersion]);

  // The versions whose code is still kept, newest first, one per code.
  const versions = useMemo<Version[]>(() => {
    const seen = new Set<string>();
    return Object.entries(actions || {})
      .filter(([, row]) => row.view === "CODE" && !!row.codeHash)
      .sort(([, a], [, b]) => b.number - a.number)
      .map(([id, row]) => ({
        deploymentId: id,
        number: row.number,
        hash: row.codeHash as string,
        isLive: row.rollback === "CURRENT",
      }))
      .filter((version) => {
        if (seen.has(version.hash)) return false;
        seen.add(version.hash);
        return true;
      });
  }, [actions]);

  const latest = source?.latest;
  const latestHash = latest ? latest.manifestSha256.slice(0, 7) : "";
  const picked = deploymentId && actions ? actions[deploymentId] : undefined;

  // What the chip names: the deployment asked for, or the newest one of the
  // latest code.
  const shown = deploymentId
    ? {
        number: picked?.number,
        hash: picked?.codeHash || "",
        isLive: picked?.rollback === "CURRENT",
      }
    : {
        number: versions.find((version) => version.hash === latestHash)?.number,
        hash: latestHash,
        isLive: latest?.pinnedAs === "CURRENT",
      };

  // The whole code as a .zip, built on demand: asks until it is ready.
  const downloadZip = async () => {
    setIsArchiving(true);
    await downloadArchive(() =>
      deploymentId
        ? archiveDeployment(app.id, deploymentId)
        : archiveSource(app.id)
    );
    setIsArchiving(false);
  };

  if (!isUpload || !canRead) {
    return (
      <div className="flex flex-col gap-4">
        <ScreenHead title="Source Code" />
        <SurfaceCard
          tone="surface"
          className="px-5 py-4 text-ds-125 text-navy-200"
        >
          {!isUpload
            ? "This app doesn't deploy from uploaded files, so there is no code to show here."
            : "Your access to this app doesn't include its code. Ask the app owner for Build & Deploy access."}
        </SurfaceCard>
      </div>
    );
  }

  const hasCode = !!latest || !!deploymentId;

  // New code is a deploy: follow it to where the build shows, as Settings does.
  const uploadDialog = canUpload ? (
    <UploadNewCodeDialog
      appId={app.id}
      open={isUploadOpen}
      onClose={() => setIsUploadOpen(false)}
      onDeployed={() => navigate(`/apps/${app.id}`)}
    />
  ) : null;

  // The latest code has a plain URL; any other is a deployment's.
  const pickVersion = (version: Version) =>
    version.hash === latestHash
      ? setSearchParams({})
      : setSearchParams({ deployment: version.deploymentId });

  return (
    // Desktop: the whole height of the window, the editor gets everything
    // below the head, and it takes back all of main's bottom padding (kept
    // for the phone's tab bar), running to the bottom of the window.
    // Phone: the page scrolls, and the viewer has a height of its own — made
    // to fit the window there, the head and the tree left the editor a sliver.
    <div className="flex flex-col lg:flex-1 lg:min-h-0 lg:-mb-24">
      <div className="flex-none flex flex-wrap items-center justify-between gap-3 mb-4 [@media(max-width:767.98px)]:hidden">
        <ScreenHead title="Source Code" className="!mb-0">
          {hasCode && shown.hash ? (
            <span className="flex items-center gap-2.5">
              <VersionPicker
                current={shown}
                versions={versions}
                onPick={pickVersion}
              />
              <LivePill isLive={shown.isLive} />
            </span>
          ) : null}
        </ScreenHead>
        {/* Everything you can do with the code — copy, download a file,
            export it all, send new code — is on the viewer's file bar, as the
            Agents code panel has it; the head keeps only the name and the
            version. Deployments are in the sidebar. Without code there is no
            viewer, so the one action is here. */}
        {canUpload && !hasCode ? (
          <DsButton
            variant="action"
            size="lg"
            className="flex-none"
            onClick={() => setIsUploadOpen(true)}
          >
            <UploadIcon size={16} /> Upload new code
          </DsButton>
        ) : null}
      </div>

      {loadError ? (
        <div
          role="alert"
          className="flex items-center gap-2 text-ds-125 text-state-err"
        >
          <AlertIcon size={14} /> {loadError}
        </div>
      ) : !source ? (
        <div className="font-courrier text-ds-115 text-navy-200">
          Loading code…
        </div>
      ) : !hasCode ? (
        <SurfaceCard
          tone="surface"
          className="px-5 py-4 text-ds-125 text-navy-200"
        >
          No code uploaded yet.
          {canUpload ? " Upload a folder to deploy it." : ""}
        </SurfaceCard>
      ) : (
        // Desktop: edge to edge — against the sidebar, the window's right side
        // and its bottom — so the viewer's rules run into edges instead of
        // stopping short of them in the middle of the page.
        <div className="h-[80vh] lg:h-auto lg:flex-1 lg:min-h-[28rem] lg:-mx-8">
          <CodeViewer
            appId={app.id}
            deploymentId={deploymentId}
            reloadKey={latest?.uploadId}
            onExport={downloadZip}
            onUpload={canUpload ? () => setIsUploadOpen(true) : undefined}
            // Editing is a deploy too: the same gate as uploading.
            canEdit={canUpload}
            onCodeChanged={() => setCodeVersion((version) => version + 1)}
            isExporting={isArchiving}
            onPhoneRows={setViewerRows}
            versions={
              shown.hash
                ? { current: shown, all: versions, onPick: pickVersion }
                : undefined
            }
          />
        </div>
      )}
      {uploadDialog}
    </div>
  );
};

export default MyAppSource;
